import React, { useCallback, useEffect, useRef, useState } from "react";
import useFocusTrap from "./useFocusTrap";
import "../ManualDeliveries.css";

let nextId = 0;

// In-page replacement for window.confirm. Starts on the safe button; Escape answers no. The toast behind it is made
// inert too, so nothing outside the dialog can be pressed while it is open.
export function ConfirmDialog({
  id,
  title,
  body,
  action,
  cancelLabel = "Keep it",
  tone = "danger",
  returnFocusTo = null,
  onAnswer,
}) {
  const ref = useRef(null);
  const cancelRef = useRef(null);
  const answerRef = useRef(onAnswer);
  answerRef.current = onAnswer;
  // The trap runs first so it remembers the opener before focus moves to the safe button.
  useFocusTrap(ref, true, { returnFocusTo, hideLiveRegions: true });
  useEffect(() => {
    if (cancelRef.current) cancelRef.current.focus();
  }, []);
  // Escape is caught on the document in the capture phase and stopped there, so no other Escape handler (the
  // panel's own, React's, a native listener) also runs while the dialog is open.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      answerRef.current(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);
  return (
    <div className="md-dialog-backdrop">
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-body`}
        className="md-dialog"
      >
        <h2 id={`${id}-title`} className="md-dialog__title">
          {title}
        </h2>
        <p id={`${id}-body`} className="md-dialog__body">
          {body}
        </p>
        <div className="md-dialog__actions">
          <button ref={cancelRef} type="button" className="md-btn" onClick={() => onAnswer(false)}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === "danger" ? "md-btn md-btn--danger" : "md-btn md-btn--primary"}
            onClick={() => onAnswer(true)}
          >
            {action}
          </button>
        </div>
      </div>
    </div>
  );
}

// [element, confirm]: render element once; confirm(options, { returnFocusTo }) resolves true only when the action
// button is pressed. returnFocusTo (element or ref) is where focus goes if the opener is gone by then. A second
// confirm while one is open answers the first with false.
export function useConfirm() {
  const [request, setRequest] = useState(null);
  const openRef = useRef(null);
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = `md-confirm-${++nextId}`;
  const answer = useCallback((value) => {
    const current = openRef.current;
    if (!current) return;
    openRef.current = null;
    setRequest(null);
    current.resolve(value);
  }, []);
  const confirm = useCallback(
    (options, { returnFocusTo = null } = {}) =>
      new Promise((resolve) => {
        if (openRef.current) openRef.current.resolve(false);
        const next = { ...options, returnFocusTo, resolve, key: (openRef.current ? openRef.current.key : 0) + 1 };
        openRef.current = next;
        setRequest(next);
      }),
    []
  );
  const element = request ? (
    <ConfirmDialog
      key={request.key}
      id={idRef.current}
      title={request.title}
      body={request.body}
      action={request.action}
      cancelLabel={request.cancelLabel}
      tone={request.tone}
      returnFocusTo={request.returnFocusTo}
      onAnswer={answer}
    />
  ) : null;
  return [element, confirm];
}
